import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const ProfileSetup: React.FC = () => {
  const { user, completeProfileSetup, refreshSession, checkHandleAvailability } = useAuth();
  const { addToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [handleStatus, setHandleStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [handleSuggestions, setHandleSuggestions] = useState<string[]>([]);
  
  // Using refs for form inputs to prevent re-renders while typing
  const displayNameRef = useRef<HTMLInputElement>(null);
  const handleRef = useRef<HTMLInputElement>(null);
  const headlineRef = useRef<HTMLInputElement>(null);
  const bioRef = useRef<HTMLTextAreaElement>(null);
  
  // Using refs for character count display to avoid re-renders when typing
  const headlineCharCountRef = useRef<HTMLDivElement>(null);
  const bioCharCountRef = useRef<HTMLDivElement>(null);
    // Handle checking with debounce
  const handleCheckTimeout = useRef<NodeJS.Timeout | null>(null);
    const checkHandle = useCallback(async (handle: string) => {
    if (!handle || handle.trim().length < 3) {
      setHandleStatus('idle');
      setHandleSuggestions([]);
      return;
    }
    
    setHandleStatus('checking');
    
    try {
      const result = await checkHandleAvailability(handle);
      if (result.available) {
        setHandleStatus('available');
        setHandleSuggestions([]);
      } else {
        setHandleStatus('taken');
        setHandleSuggestions(result.suggestions || []);
      }
    } catch (error) {
      setHandleStatus('invalid');
      setHandleSuggestions([]);
    }
  }, [checkHandleAvailability]);
  
  const handleHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Clear previous timeout
    if (handleCheckTimeout.current) {
      clearTimeout(handleCheckTimeout.current);
    }
    
    // Debounce the handle check
    handleCheckTimeout.current = setTimeout(() => {
      checkHandle(value);
    }, 500);
  };

  // Update DOM elements directly for character counts to avoid re-renders
  const updateCharCount = (type: 'headline' | 'bio', value: string) => {
    if (type === 'headline' && headlineCharCountRef.current) {
      headlineCharCountRef.current.textContent = `${value.length}/100 characters`;
    } else if (type === 'bio' && bioCharCountRef.current) {
      bioCharCountRef.current.textContent = `${value.length}/500 characters`;
    }
  };  // Initialize form with user's Google name and generate handle based on name
  useEffect(() => {
    const initializeForm = async () => {
      if (user && displayNameRef.current) {
        displayNameRef.current.value = user.name;
        
        // Generate handle based on user's name if handle field is empty
        if (handleRef.current && !handleRef.current.value) {
          try {
            // Use the name-based handle generation by calling the backend directly
            // We'll generate it based on the user's name, similar to the skip functionality
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/handle/generate-from-name`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ name: user.name }),
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.handle) {
                handleRef.current.value = data.handle;
                // Check the generated handle immediately
                checkHandle(data.handle);
              }
            } else {
              console.error('Failed to generate handle from name');
            }
          } catch (error) {
            console.error('Failed to generate handle from name:', error);
          }
        }
      }
    };
    
    initializeForm();
  }, [user, checkHandle]);const handleComplete = async (isSkipping = false) => {
    setIsSaving(true);
    
    try {
      let profileData;
      
      if (isSkipping) {
        // When skipping, only send the user's name and skip flag
        profileData = {
          name: user?.name || '',
          handle: '', // Empty handle - backend will generate one
          headline: '',
          bio: '',
          isSkipping: true
        };
      } else {
        // When completing, use the form data
        profileData = {
          name: displayNameRef.current?.value?.trim() || '',
          handle: handleRef.current?.value?.trim() || '',
          headline: headlineRef.current?.value?.trim() || '',
          bio: bioRef.current?.value?.trim() || '',
          isSkipping: false
        };
        
        // Validate required fields for complete setup
        if (!profileData.name) {
          addToast('Please enter your name', 'error');
          setIsSaving(false);
          return;
        }
      }

      const success = await completeProfileSetup(profileData);
        if (success) {
        // Refresh session to get updated user data
        await refreshSession();
        addToast(isSkipping ? 'Profile setup skipped - you can complete it later!' : 'Profile setup completed successfully!', 'success');
      } else {
        addToast('Failed to complete profile setup. Please try again.', 'error');
      }} catch (error) {
      console.error('Error completing profile setup:', error);
      addToast('An error occurred while setting up your profile.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-8">
      <main className="flex flex-col items-center justify-center w-full flex-1 px-4 md:px-20">
        <div className="w-full max-w-2xl">
          <Card className="bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user.picture} alt="Profile Picture" />
                  <AvatarFallback className="text-xl bg-parchment-100 text-custom-blue-900">
                    {user.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
              <CardTitle className="text-2xl text-custom-blue-900">
                Welcome to MythDimension!
              </CardTitle>
              <p className="text-custom-blue-700 mt-2">
                Let's set up your profile to get started. You can always change this later.
              </p>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-custom-blue-900">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  ref={displayNameRef}
                  type="text"
                  maxLength={50}
                  className="w-full px-3 py-2 border border-parchment-300 rounded-md bg-paper text-custom-blue-900 placeholder-custom-blue-500 focus:outline-none focus:ring-2 focus:ring-custom-blue-500"
                  placeholder="Enter your display name"
                />
                <p className="text-xs text-custom-blue-600 mt-1">
                  This is how other writers will see your name
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-custom-blue-900">
                  Handle <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-custom-blue-600 text-sm">@</span>
                  </div>
                  <input
                    ref={handleRef}
                    type="text"
                    maxLength={20}
                    onChange={handleHandleChange}
                    className="w-full pl-8 pr-3 py-2 border border-parchment-300 rounded-md bg-paper text-custom-blue-900 placeholder-custom-blue-500 focus:outline-none focus:ring-2 focus:ring-custom-blue-500"
                    placeholder="yourhandle"
                  />
                  {handleStatus === 'checking' && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <div className="animate-spin h-4 w-4 border-2 border-custom-blue-600 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-custom-blue-600 mt-1">
                  Your unique handle (3-20 characters, letters, numbers, and underscores only)
                </p>
                {handleStatus === 'available' && (
                  <p className="text-sm text-green-600 mt-1">
                    ✓ Handle is available!
                  </p>
                )}
                {handleStatus === 'taken' && (
                  <div className="mt-1">
                    <p className="text-sm text-red-600">
                      ✗ Handle is already taken
                    </p>
                    {handleSuggestions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-custom-blue-600">Suggestions:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {handleSuggestions.map((suggestion) => (
                            <button
                              key={suggestion}
                              type="button"
                              onClick={() => {
                                if (handleRef.current) {
                                  handleRef.current.value = suggestion;
                                  checkHandle(suggestion);
                                }
                              }}
                              className="px-2 py-1 text-xs bg-custom-blue-100 text-custom-blue-700 rounded hover:bg-custom-blue-200 transition-colors"
                            >
                              @{suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {handleStatus === 'invalid' && (
                  <p className="text-sm text-red-600 mt-1">
                    ✗ Invalid handle format
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-custom-blue-900">
                  Headline
                </label>
                <input
                  ref={headlineRef}
                  type="text"
                  maxLength={100}
                  onChange={(e) => updateCharCount('headline', e.target.value)}
                  className="w-full px-3 py-2 border border-parchment-300 rounded-md bg-paper text-custom-blue-900 placeholder-custom-blue-500 focus:outline-none focus:ring-2 focus:ring-custom-blue-500"
                  placeholder="A short headline about yourself (optional)"
                />
                <div ref={headlineCharCountRef} className="text-xs text-custom-blue-600 mt-1">
                  0/100 characters
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-custom-blue-900">
                  Bio
                </label>
                <textarea
                  ref={bioRef}
                  maxLength={500}
                  onChange={(e) => updateCharCount('bio', e.target.value)}
                  className="w-full px-3 py-2 border border-parchment-300 rounded-md bg-paper h-24 resize-none text-custom-blue-900 placeholder-custom-blue-500 focus:outline-none focus:ring-2 focus:ring-custom-blue-500"
                  placeholder="Tell other writers about yourself (optional)"
                />
                <div ref={bioCharCountRef} className="text-xs text-custom-blue-600 mt-1">
                  0/500 characters
                </div>
              </div>

              <div className="flex justify-between items-center pt-4">
                <p className="text-sm text-custom-blue-600">
                  You can skip this and complete your profile later
                </p>                <div className="space-x-3">
                  <Button 
                    variant="outline"
                    onClick={() => handleComplete(true)}
                    disabled={isSaving}
                    className="bg-parchment-100 border-parchment-300 text-custom-blue-900 hover:bg-parchment-200"
                  >
                    Skip for now
                  </Button>
                  <Button 
                    onClick={() => handleComplete(false)}
                    disabled={isSaving}
                    className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white"
                  >
                    {isSaving ? 'Setting up...' : 'Complete Setup'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ProfileSetup;
